<template>
  <div class="checklist-item">
    <DraggingIndication />
    <div class="v-centerer">
      <EditableLabel
        v-model="item.text"
        class="checklist-item__title"
        :legend="$t('message.GW_CL_ITEM_NB', { nb: (index + 1) })"
        :placeholder="$t('message.CLICK_TO_TYPE')"
        :disabled="disabled"
        use-textarea
        @input="editItem('text')"
      />
      <Badge :color="item.files.length == 0 ? '#999999' : '#00adee'" @click.native="editPictures">
        <span slot="badge">{{ item.files.length }}</span>
        <div class="generic-border generic-border--icon-button generic-border--blue-border">
          <Icon class="cl-item-icon">{{ galleryMedium }}</Icon>
        </div>
      </Badge>
      <div
        v-if="!disabled"
        class="generic-border generic-border--icon-button generic-border--red ml10em"
        @click="deleteItem(index, item)"
       >
        <Icon>
          {{ deleteMedium }}
        </Icon>
      </div>
    </div>
    <div class="type-choice">
      <span class="legend">
        {{ `${$t('message.GW_CL_ITEM_TYPE')}${$t('message.TWO_DOTS')}` }}
      </span>
      <RadioGroup
        v-model="item.type"
        :items="types"
        :disabled="disabled"
        @input="editType"
      >
        <EditableLabelRadio
          v-if="item.type === GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR"
          ref="gwIndicatorEdit"
          :slot="1"
          v-model="item.indicator"
          label-key="name"
          :placeholder="$t('message.NONE')"
          :items="allIndicators"
          :disabled="disabled"
          @input="editIndicator(GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR)"
          @close="closeIndicatorEdition"
        />
        <EditableLabelRadio
          v-if="item.type === GW_CHECKLIST_ITEM_TYPE.DMP_INDICATOR"
          ref="indicatorEdit"
          :slot="2"
          v-model="item.indicator"
          label-key="name"
          :placeholder="$t('message.NONE')"
          :items="allIndicators"
          :disabled="disabled"
          @input="editIndicator(GW_CHECKLIST_ITEM_TYPE.DMP_INDICATOR)"
          @close="closeIndicatorEdition"
        />
      </RadioGroup>
    </div>

    <!-- Files edition -->
    <FileViewer
      v-if="fileViewerVisible"
      v-model="item.files"
      pathname="gwClItems/"
      editable
      @input="editItem('files')"
      @close="fileViewerVisible = false"
    />

    <EditableLabelRadio
      ref="newIndicatorThemeSelection"
      :items="newIndicatorThemeOptions"
      label-key="name"
      no-text
      @input="newIndicatorTheemSelected"
    />

    <Modal
      v-if="newIndicator"
      :value="true"
      xlarge
      @input="deleteNewIndicator"
    >
      <div>
        <IndicatorsSettingsItem
          :indicator="newIndicator"
          :all-themes="themes"
          :class="{ expanded: true }"
          :refreshing="newIndicatorOngoingRequest"
          always-expanded
          @edit="editNewIndicator"
          @delete="deleteNewIndicator"
        />
        <div class="h-v-centerer">
          <button class="generic-border generic-border--blue generic-border--text-btn" @click="saveNewIndicator">
            {{ $t('message.OK') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
/**
 * Editable checklist item that allows the user to edit the displayrd text and add/remove pictures
 *
 * @vue-prop {Object} item - Item data as an object { id, text, pictures: [] }
 * @vue-prop {Number} index - Index in the list of items (Optional)
 * @vue-prop {Boolean} disabled - Whether edition should be prevented
 *
 * @vue-event {} delete - Item has been deleted
 * @vue-event {} edit - Item text has been edited
 */
import Vue from 'vue';
import toastr from 'toastr';
// State
import { mapState } from 'vuex';
import { FETCH_INDICATOR_THEMES } from '@/store/action-types';
import { GET_INDICATOR_THEMES } from '@/store/mutation-types';
// Components
import Badge from '@/components/badge.vue';
import DraggingIndication from '@/components/dragging-indication.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import FileViewer from '@/components/file-system/file-viewer.vue';
import Icon from '@/components/icon.vue';
import IndicatorsSettingsItem from '@/components/indicators-settings-item.vue';
import Modal from '@/components/modal.vue';
import RadioGroup from '@/components/radio-group.vue';
// Utils
import { GW_CHECKLIST_ITEM_TYPE } from '@/utils/gw-utils';
import { calert } from '@/utils/main';
import { deleteMedium, galleryMedium } from '@/assets/icons/';


export default {

  name: 'GwEditionChecklistItem',

  components: {
    Badge,
    DraggingIndication,
    EditableLabel,
    EditableLabelRadio,
    FileViewer,
    Icon,
    IndicatorsSettingsItem,
    Modal,
    RadioGroup,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      default: 1,
    },
    disabled: Boolean,
  },

  data() {
    return {
      deleteMedium,
      galleryMedium,
      GW_CHECKLIST_ITEM_TYPE,
      newIndicator: null,
      newIndicatorOngoingRequest: false,
      fileViewerVisible: false,
    };
  },

  computed: {
    types() {
      const types = [
        { id: GW_CHECKLIST_ITEM_TYPE.OKNOK, label: this.$t('message.GW_CL_ITEM_TYPE_OKNOK') },
        { id: GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR, label: this.$t('message.GW_CL_ITEM_TYPE_GW_INDICATOR') },
      ];
      if (Number(this.currentTeam.gwDMPIndicators) === 1) {
        return [
          ...types,
          { id: GW_CHECKLIST_ITEM_TYPE.DMP_INDICATOR, label: this.$t('message.GW_CL_ITEM_TYPE_DMP_INDICATOR') },
        ];
      }
      return types;
    },
    /** Array: All themes to pick from when creating a new indicator */
    newIndicatorThemeOptions() {
      const showGw = this.item.type === GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR ? 1 : 0;
      return [{
        title: this.$t('message.PICK_THEME'),
        radioItems: this.themes.filter(theme => !theme.indicators.some(i => i.isGw !== showGw)),
      }];
    },
    ...mapState({
      themesLoading: state => state[GET_INDICATOR_THEMES.loadingKey],
      themes: state => state[GET_INDICATOR_THEMES.stateKey] || [],
    }),
    /** Indicators to pick from */
    allIndicators() {
      const themes = this.themes.map(theme => ({
        title: theme.name,
        radioItems: theme.indicators.filter(
          indic => (indic.isGw === 1) === (this.item.type === GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR),
        ),
      }));
      const addIndic = this.$t('message.ADD_INDICATOR');
      return [
        { id: 0, title: addIndic, radioItems: [{ id: 0, name: addIndic }] },
        ...themes.filter(({ radioItems }) => radioItems.length > 0),
      ];
    },
  },

  mounted() {
    if (!this.themesLoading) {
      this.$store.dispatch(FETCH_INDICATOR_THEMES);
    }
  },

  methods: {
    /** Edit the text of a checklist item */
    editItem(fieldKeys) {
      if (!(fieldKeys instanceof Array)) {
        fieldKeys = [fieldKeys];
      }
      const params = { id: this.item.id };
      fieldKeys.forEach((key) => {
        params[key] = this.item[key];
      });
      Vue.axios({
        method: 'post',
        url: '/server/gembaWalks/checkLists/editItem.php',
        data: params,
      }).then(({ data }) => {
        if (data === 1) {
          toastr.success(this.$t('message.GW_SAVED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
      this.$emit('edit');
    },
    /** Edit the checklist item type */
    editType() {
      if (!(this.item.indicator)) {
        if (this.item.type === GW_CHECKLIST_ITEM_TYPE.DMP_INDICATOR) {
          this.$nextTick(() => { this.$refs.indicatorEdit.edit(); });
        } else if (this.item.type === GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR) {
          this.$nextTick(() => { this.$refs.gwIndicatorEdit.edit(); });
        }
      } else {
        this.editItem('type');
      }
    },
    /** Edit the indicator param and set the type to 2 */
    editIndicator(type) {
      if (this.item.indicator === 0) {
        // User clicked on 'Add an indicator'
        this.createNewIndicator();
        return;
      }
      this.item.type = type;
      this.editItem(['type', 'indicator']);
    },
    /** Indicator edition was closed, no value picked */
    closeIndicatorEdition() {
      if (this.item.indicator <= 0) {
        this.item.type = GW_CHECKLIST_ITEM_TYPE.OKNOK;
      }
    },
    /** Permanently delete an item from a checklist */
    deleteItem(index, item) {
      const buttons = [
        {
          title: this.$t('message.GW_CL_DELETE'),
          sClass: 'red',
          action: () => {
            this.confirmDeleteItem(index, item);
          },
        },
        { title: this.$t('message.CANCEL') },
      ];
      if (this.item.type === GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR) {
        buttons.splice(0, 0, {
          title: this.$t('message.GW_CL_DELETE_ORPHAN'),
          sClass: 'red',
          action: () => {
            this.confirmDeleteItem(index, item, true);
          },
        });
      }
      calert(this.$t('message.GW_CONFIRM_DELETE_ITEM'), buttons);
    },
    confirmDeleteItem(index, item, deleteOrphanIndicators = false) {
      Vue.axios({
        method: 'post',
        url: '/server/gembaWalks/checkLists/deleteItem.php',
        data: { id: item.id, deleteOrphanIndicators },
      }).then(({ data }) => {
        if (data === 1) {
          this.$emit('delete');
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Create a new indicator for this item */
    createNewIndicator() {
      this.$refs.newIndicatorThemeSelection.edit();
    },
    /** Theme has been selected, create the new indicator */
    newIndicatorTheemSelected(theme) {
      this.$refs.newIndicatorThemeSelection.stopEdit();
      this.$http.post('/server/indicators/add.php', {
        theme,
        name: this.item.text,
        isGw: this.item.type === GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR,
      })
        .then((response) => {
          if (response && response.data instanceof Array) {
            const allIndicators = response.data.flatMap(th => th.indicators);
            this.newIndicator = allIndicators.reduce((latest, indic) => (latest.id > indic.id ? latest : indic));
            this.newIndicator.expanded = true;
          }
        });
    },
    /** Save the newly created indicator */
    saveNewIndicator() {
      this.item.indicator = this.newIndicator.id;
      this.editItem(['indicator', 'type']);
      this.newIndicator = null;
      this.$store.dispatch(FETCH_INDICATOR_THEMES);
    },
    editNewIndicator(indic, fieldKeys, fieldValues) {
      if (fieldKeys === 'isGw') {
        return;
      }
      this.newIndicatorOngoingRequest = true;
      if (!(fieldKeys instanceof Array)) {
        fieldKeys = [fieldKeys];
      }
      const params = { id: indic.id };
      fieldKeys.forEach((key) => {
        params[key] = fieldValues ? fieldValues[key] : indic[key];
      });
      this.$http.post('/server/indicators/edit.php', params).then((response) => {
        this.newIndicatorOngoingRequest = false;
        if (!response || !(response.data instanceof Array)) {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** User chose to close the edition or to delete the newly created indicator */
    deleteNewIndicator() {
      this.$http.post('/server/indicators/delete.php', { id: this.newIndicator.id }).then((response) => {
        if (response && response.data instanceof Array) {
          this.newIndicator = null;
        }
      });
    },
    editPictures() {
      if (!this.disabled) {
        this.fileViewerVisible = true;
      }
    },
  },
};
</script>

<style lang="scss">
.checklist-item {
  padding: 0px 5px;
  position: relative;
  padding-left: 2rem;

  .dragging-indication {
    position: absolute;
    left: 0px;
    top: 50%;
    transform: translateY(-50%);
  }

  .badge__badge {
    top: 0px;
    right: 0px;
  }

  &__title {
    flex: 1;
    min-width: 0px;
    margin-right: 3rem;
  }

  &::after {
    display: block;
    content: "";
    width: 80%;
    margin: 10px auto;
    height: 1px;
    background: gray;
  }
  .type-choice {
    margin-top: .7rem;

    .radio-wrapper {

      .editable-label-radio {
        margin-left: 1em;

        .editable-label-text {
          font-size: 0.9em;
          font-style: italic;
        }
      }
    }
  }
  .gw-new-indicator .dark-overlay-content {
    text-align: left;

    .save-new-indicator {
      margin-top: 10px;
      margin-left: 50%;
      transform: translateX(-50%);
    }
  }
}
</style>
